import { useEffect, useRef } from 'react';

/*
 * Animated AI avatar — a glowing ring of flat colour blobs orbiting a hollow centre, in the accent
 * colour. Idle: slow drift; speaking: spins faster + blobs pulse + glow brightens. Ported verbatim
 * from Sparked v2's shared `AiAvatar` (apps/web) so the Code Box co-author looks identical to Sparky.
 * Relies on the `orb-glow` / `orb-glow-fast` keyframes in index.css.
 */

/* smooth closed path through points (Catmull-Rom → cubic bezier) */
function smoothClosed(pts: [number, number][]): string {
  const n = pts.length;
  if (!n) return '';
  let d = `M${pts[0]![0].toFixed(2)},${pts[0]![1].toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n]!, p1 = pts[i]!, p2 = pts[(i + 1) % n]!, p3 = pts[(i + 2) % n]!;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d + 'Z';
}
/* an organic blob centered at (cx,cy): each vertex radius wobbles via summed sines. */
function blobPath(cx: number, cy: number, r: number, points: number, t: number, seed: number, amp: number, rot = 0): string {
  const pts: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const a = (i / points) * Math.PI * 2 + rot;
    const w = 1 + amp * (Math.sin(t * 1.3 + i * 1.9 + seed) * 0.62 + Math.sin(t * 0.8 + i * 2.7 + seed * 1.7) * 0.38);
    pts.push([cx + Math.cos(a) * r * w, cy + Math.sin(a) * r * w]);
  }
  return smoothClosed(pts);
}
const ORB_BLOBS = [
  { rf: 1.0, pts: 7, seed: 0.0, base: 0, fill: 'rgb(var(--color-accent))' },
  { rf: 0.92, pts: 6, seed: 2.1, base: 130, fill: 'color-mix(in srgb, rgb(var(--color-accent)) 45%, #fff)' },
  { rf: 0.84, pts: 8, seed: 4.2, base: 250, fill: 'rgb(var(--color-muted))' },
];

export function AiAvatar({ speaking = false, size = 112 }: { speaking?: boolean; size?: number }) {
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const holeRef = useRef<SVGPathElement | null>(null);
  const speakingRef = useRef(speaking);
  speakingRef.current = speaking;
  useEffect(() => {
    let raf = 0; let start = 0;
    const loop = (now: number) => {
      if (!start) start = now;
      const t = (now - start) / 1000;
      const spk = speakingRef.current;
      const spd = spk ? 2.4 : 1;
      const amp = spk ? 0.4 : 0.3;
      const pulse = 1 + (spk ? 0.06 : 0.03) * Math.sin(t * (spk ? 4.5 : 2.4));
      for (let i = 0; i < ORB_BLOBS.length; i++) {
        const b = ORB_BLOBS[i]!;
        const rot = t * (spk ? 0.6 : 0.22) * (i % 2 === 0 ? 1 : -1) + b.seed;
        pathRefs.current[i]?.setAttribute('d', blobPath(50, 50, 32 * b.rf * pulse, b.pts, t * spd, b.seed, amp, rot));
      }
      holeRef.current?.setAttribute('d', blobPath(50, 50, 15.5, 8, t * (spk ? 1 : 0.5) + 10, 7, 0.045, t * 0.12));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} aria-hidden>
      <div className={`absolute inset-0 rounded-full ${speaking ? 'orb-glow-fast' : 'orb-glow'}`} style={{ background: 'radial-gradient(circle at 50% 50%, rgb(var(--color-accent) / .4), transparent 70%)', filter: `blur(${size * 0.14}px)` }} />
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
        {ORB_BLOBS.map((b, i) => (
          <path key={i} ref={el => { pathRefs.current[i] = el; }} fill={b.fill} fillOpacity={0.72} />
        ))}
        <path ref={holeRef} fill="rgb(var(--color-surface))" />
      </svg>
    </div>
  );
}

export default AiAvatar;
